<template>
  <div>
    <v-breadcrumbs
      class="pt-3 pl-7"
      :items="[{ text: 'Decorations', exact: true,  to: { name: 'decorations' } }, { text: oldDecoration.description }]"
    >
    </v-breadcrumbs>
    <v-row
      class="mt-0"
    >

      <v-col
        cols="12"
        md="5"
      >

        <v-card
          flat
          class="mx-3 pb-9"
        >
          <v-toolbar
            dark
            class="tertiary"
            dense
            elevation="0"
          >
            <v-toolbar-title>Decoration</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="editDecorationClick()"
              :disabled="!user.can('decoration.update')"
            >
              <v-icon color="secondary" small>fas fa-edit</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text>

            <dl class="row table mt-5">
              <dt class="col-5">Technique:</dt>
              <dd class="col-7">{{ oldDecoration.technique?.description }}</dd>
              <dt class="col-5">Description:</dt>
              <dd class="col-7">{{ oldDecoration.description }}</dd>
              <dt class="col-5">Slug:</dt>
              <dd class="col-7">{{ oldDecoration.slug }}</dd>
              <dt class="col-5">Width:</dt>
              <dd class="col-7">{{ oldDecoration.width }} mm</dd>
              <dt class="col-5">Height:</dt>
              <dd class="col-7">{{ oldDecoration.height }} mm</dd>
            </dl>

          </v-card-text>

          <decoration-dialog
            v-model="decorationDialog"
            :model="decoration"
            :errors="decorationErrors"
            :techniques="techniques"
            :error="decorationError"
            @save="saveDecorationClick()"
            @delete="(item) => deleteDecorationItem(item)"
          ></decoration-dialog>

        </v-card>


      </v-col>


      <v-col
        cols="12"
        md="7"
      >
        <v-card
          flat
          class="mx-3 pb-9"
        >
          <v-toolbar
            dark
            class="tertiary"
            dense
            elevation="0"
          >
            <v-toolbar-title>Services</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-text-field
              dense
              background-color="primary"
              v-model="serviceFilter.query"
              label="Search..."
              single-line
              hide-details
              solo
              @keyup="searchKeyUp()"
            ></v-text-field>
            <v-btn
              icon
              size="secondary"
              @click="newServiceClick()"
              :disabled="!user.can('decoration-service.create')"
            >
              <v-icon small>fas fa-plus</v-icon>
            </v-btn>
          </v-toolbar>

          <v-data-table
            :class="{ 'mt-5': true, 'clickable': user.can('decoration-service.update') }"          
            :headers="serviceHeaders"
            :items="services"
            :loading="serviceIsSearching"
            :options.sync="serviceOptions"
            :server-items-length="serviceTotalCount"
            :footer-props="{ 'items-per-page-options': [5, 10, 50] }"
            @click:row="rowServiceClick"
          >

            <template v-slot:item.type="{ value }">
              {{ formatType(value) }}
            </template>

            <template v-slot:item.quantity="{ value }">
              {{ value ?? '-' }}
            </template>

          </v-data-table>

          <decoration-service-dialog
            v-model="serviceDialog"
            :model="service"
            :decorationServiceTypes="decorationServiceTypes"
            :errors="serviceErrors"
            @save="saveServiceClick()"
            @delete="(item) => deleteServiceItem(item)"
          ></decoration-service-dialog>

        </v-card>
      </v-col>
    </v-row>


  </div>
</template>

<script>
import { inject, ref, watch } from 'vue'
import DecorationDialog from '@/components/DecorationDialog'
import DecorationServiceDialog from '@/components/DecorationServiceDialog'
import { useRouter, useRoute } from 'vue-router/composables'

export default {
  components: {
    DecorationDialog,
    DecorationServiceDialog,
  },
  setup() {
    const api = inject('api')
    const { user } = api
    const router = useRouter()
    const route = useRoute()

    const techniqueCollection = api.collection('techniques')
    const { items: techniques } = techniqueCollection.all()

    const decorationModel = api.model('decorations/:id', {
      params: {
        expand: 'technique',
      },
    })

    const { attributes: decoration, errors: decorationErrors, oldAttributes: oldDecoration } = decorationModel
    const decorationDialog = ref(false)
    const decorationError = ref()

    function saveDecorationClick() {
      decorationError.value = null
      decorationModel.save().then(() => {
        decorationDialog.value = false
      })
    }

    function deleteDecorationItem(item) {
      decorationError.value = null
      if (user.can('decoration.delete') && item?.id) {
        decorationModel.delete(item.id).then(() => {
          decorationModel.clear()
          decorationDialog.value = false
          router.push({
            name: 'decorations'
          })
        }).catch(() => {
          decorationError.value = 'This decoration is used in a preset surface decoration and cannot be deleted.'
        })
      }

    }

    const serviceCollection = api.collection('decoration-services', {
      params: {
        expand: 'decoration',
      },
      filter: {
        decoration_id: 'eq',
        query: 'query:article_number',
      },
    })

    const { items: services, filter: serviceFilter, options: serviceOptions, totalCount: serviceTotalCount, isSearching: serviceIsSearching } = serviceCollection

    const serviceHeaders = [
      { text: 'Decoration', value: 'decoration.description' },
      { text: 'Type', value: 'type' },
      { text: 'Article Number', value: 'article_number' },
      { text: 'Quantity', value: 'quantity' },
    ]

    const searchKeyUp = serviceCollection.debounceSearch(300)

    const serviceModel = api.model('decoration-services/:id')

    const { attributes: service, errors: serviceErrors } = serviceModel
    const serviceDialog = ref(false)

    function newServiceClick() {
      serviceModel.clear().setAttribute('decoration_id', decoration.value.id)
      serviceDialog.value = true
    }

    function saveServiceClick() {
      serviceModel.save().then(() => {
        serviceCollection.search()
        serviceDialog.value = false
      })
    }

    function rowServiceClick(item) {
      if (user.can('decoration-service.update')) {
        serviceModel.clear().populate(item)
        serviceDialog.value = true
      }
    }

    function editDecorationClick() {
      decorationError.value = null
      decorationModel.reset()
      decorationDialog.value = true
    }

    function deleteServiceItem(serviceItem) {
      if (user.can('decoration-service.delete') && serviceItem?.id) {
        serviceModel.delete(serviceItem.id).then(() => {
          serviceModel.clear()
          serviceCollection.search()
          serviceDialog.value = false
        })
      }
    }


    watch(() => route, ({ params }) => {
      if (params.id) {
        decorationModel.find(params.id).catch(() => {
          router.push({
            name: 'decorations',
          })
        })
        serviceCollection.setFilter('decoration_id', params.id).search()
      }
    }, { immediate: true })

    const decorationServiceTypes = [
        { value: 'fixed', text: 'Fixed' },
        { value: 'repeat', text: 'Repeat' },
    ]

    function formatType(type) {
        return decorationServiceTypes.find(decorationServiceType => decorationServiceType.value === type)?.text ?? null
    }


    return {
      user: api.user,

      decoration,
      decorationErrors,
      oldDecoration,
      decorationDialog,
      editDecorationClick,
      saveDecorationClick,

      services,
      serviceHeaders,
      serviceFilter,
      serviceOptions,
      serviceTotalCount,
      serviceIsSearching,

      service,
      serviceErrors,
      serviceDialog,
      newServiceClick,
      saveServiceClick,
      rowServiceClick,
      deleteServiceItem,

      searchKeyUp,

      techniques,
      decorationServiceTypes,
      formatType,
      deleteDecorationItem,
      decorationError,
    }

  },
}
</script>

<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

dl.table dt, dl.table dd {
  padding-top: 2px;
  padding-bottom: 2px;
}


</style>